/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import Text from '@Text';
import { loadGoogleMapsScript } from './googleMaps.helpers';
import { directionColour } from './solarPotential.helpers';
import { resolveClientApiCopy, resolveLanguage } from './copy/clientApiCopy';
import makeSolarMapStyles from './makeSolarMapStyles';

const ROOF_ZOOM = 19;

// Energy reads in kWh under 1,000 and MWh at or above, with a space before the
// unit.
const formatEnergy = (amount = 0) =>
  amount >= 1000
    ? `${(amount / 1000).toFixed(1)} MWh`
    : `${Math.round(amount)} kWh`;

const el = (tag, cssText, text) => {
  const node = document.createElement(tag);
  if (cssText) node.style.cssText = cssText;
  if (text !== undefined) node.textContent = String(text);
  return node;
};

// The "Segment N + direction badge" header, as a DOM node for the InfoWindow's
// headerContent. Putting the title there (rather than in the body) makes Google
// lay it out inline with the close button, which removes the blank strip its
// default empty header leaves above the content. Values go through textContent,
// so response data can never inject markup.
const buildPopupHeader = (side, index, copy) => {
  const header = el(
    'div',
    'display:flex;align-items:center;gap:6px;font-size:13px;font-weight:600;color:#000'
  );
  header.appendChild(el('span', null, `${copy.solar.segment} ${index + 1}`));
  header.appendChild(
    el(
      'span',
      `font-size:10px;font-weight:600;color:#fff;padding:2px 8px;border-radius:10px;background:${
        directionColour[side.direction]
      }`,
      side.direction
    )
  );
  return header;
};

// The stats grid, as a DOM node for setContent. Values go through textContent.
const buildPopupBody = (side) => {
  const stat = (value, label) => {
    const cell = el('div');
    cell.appendChild(
      el('div', 'font-size:13px;font-weight:600;color:#000', value)
    );
    cell.appendChild(el('div', 'font-size:10px;color:#666', label));
    return cell;
  };

  const grid = el(
    'div',
    'min-width:180px;display:grid;grid-template-columns:1fr 1fr;gap:6px'
  );
  grid.appendChild(stat(side.panels, 'Panels'));
  grid.appendChild(stat(formatEnergy(side.energy?.amount), 'Energy/yr'));
  grid.appendChild(stat(`${Math.round(side.area?.total ?? 0)}m²`, 'Area'));
  grid.appendChild(stat(`${Math.round(side.pitch?.amount ?? 0)}°`, 'Pitch'));
  return grid;
};

const SolarMap = ({ sides = [], localeCode }) => {
  const theme = useTheme();
  const classes = makeSolarMapStyles({ theme });
  const mapContainerRef = useRef(null);
  const markersRef = useRef([]);
  const [isUnavailable, setIsUnavailable] = useState(false);

  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

  const displayed = sides ?? [];
  const isEmpty = displayed.length === 0;

  useEffect(() => {
    if (!apiKey || isEmpty) {
      if (!apiKey) setIsUnavailable(true);
      return undefined;
    }

    let isActive = true;

    loadGoogleMapsScript(apiKey, resolveLanguage(localeCode))
      .then(() => {
        if (!isActive || !mapContainerRef.current) return;

        const { google } = window;
        const map = new google.maps.Map(mapContainerRef.current, {
          center: {
            lat: displayed[0]?.latitude ?? 0,
            lng: displayed[0]?.longitude ?? 0,
          },
          zoom: ROOF_ZOOM,
          mapTypeId: 'satellite',
          disableDefaultUI: true,
          zoomControl: true,
        });

        // One shared popup, so opening a marker's popup closes any other's.
        const info = new google.maps.InfoWindow();
        const copy = resolveClientApiCopy(localeCode);

        // Number over the displayed faces so markers read 1..n with no gaps.
        markersRef.current = displayed.map((side, index) => {
          const marker = new google.maps.Marker({
            position: { lat: side.latitude, lng: side.longitude },
            map,
            label: {
              text: String(index + 1),
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: '600',
            },
            title: side.direction,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: directionColour[side.direction],
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            },
          });
          marker.addListener('click', () => {
            info.setHeaderContent(buildPopupHeader(side, index, copy));
            info.setContent(buildPopupBody(side));
            info.open({ map, anchor: marker });
          });
          return { marker, info };
        });
      })
      .catch(() => {
        if (isActive) setIsUnavailable(true);
      });

    return () => {
      isActive = false;
      markersRef.current.forEach(({ marker }) => marker.setMap(null));
      markersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sides]);

  // Every face hidden is a valid outcome: the step still renders and the journey
  // continues, so show a neutral empty state, not the load-failure message
  // reserved for a missing key or a failed script load.
  if (isEmpty) {
    return (
      <Box css={classes.unavailable} data-testid="solarMapEmpty">
        <Text variant="text">
          {resolveClientApiCopy(localeCode).solar.mapEmpty}
        </Text>
      </Box>
    );
  }

  if (isUnavailable) {
    return (
      <Box css={classes.unavailable} data-testid="solarMapUnavailable">
        <Text variant="text">
          {resolveClientApiCopy(localeCode).solar.mapUnavailable}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      ref={mapContainerRef}
      css={classes.map}
      data-testid="solarMapContainer"
    />
  );
};

export default SolarMap;
